/**
 * Messaging API service. Calls REST endpoints for conversations and messages.
 * Uses VITE_API_BASE_URL and auth token from localStorage (user.tokens.idToken).
 */

const AWS_API_BASE_URL_FALLBACK = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
const API_BASE = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || AWS_API_BASE_URL_FALLBACK;

function base(path) {
  const p = String(path || '').startsWith('/') ? path : `/${path}`;
  return `${API_BASE.replace(/\/+$/, '')}${p}`;
}

function getToken() {
  try {
    const raw = localStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return u?.tokens?.idToken || u?.tokens?.IdToken || u?.idToken || u?.IdToken || '';
  } catch {
    return '';
  }
}

function headers(auth = true) {
  const h = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = getToken();
    if (t) h.Authorization = `Bearer ${t}`;
  }
  return h;
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const res = await fetch(base(path), {
    method,
    headers: headers(auth),
    body: body != null ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `API Error: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024; // 25MB

async function doUploadAttachment(file) {
  const name = file.name || 'file';
  const ext = name.includes('.') ? name.split('.').pop().replace(/[^a-zA-Z0-9]/g, '') : '';
  const contentType = file.type || 'application/octet-stream';
  const presign = await request('/uploads/presign', {
    method: 'POST',
    body: { kind: 'messages', contentType, ext: ext ? `.${ext}` : '' },
  });
  const { putUrl, key } = presign;
  if (!putUrl || !key) throw new Error('Presign failed');
  const putRes = await fetch(putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!putRes.ok) throw new Error('Upload failed');
  return { key, name, type: contentType, size: file.size };
}

export const messagingAPI = {
  async getConversations(options = {}) {
    const archived = options.archived === true;
    const limit = Math.max(1, Math.min(100, Number(options.limit) || 50));
    const filter = options.filter || null;
    const q = new URLSearchParams();
    if (archived) q.set('archived', 'true');
    q.set('limit', String(limit));
    if (filter && filter !== 'focused' && filter !== 'archived') {
      q.set('filter', filter);
    }
    const path = `/me/conversations?${q.toString()}`;
    const data = await request(path);
    return data.conversations || [];
  },

  async getConversation(conversationId) {
    const data = await request(`/me/conversations/${encodeURIComponent(conversationId)}`);
    return data.conversation || null;
  },

  async createConversation({ participant_ids, is_group, group_name, conversation_id } = {}) {
    const body = {
      participant_ids: participant_ids || [],
      is_group: !!is_group,
      group_name: group_name || null,
      conversation_id: conversation_id || null,
    };
    const data = await request('/me/conversations', { method: 'POST', body });
    return { conversation: data.conversation, created: !!data.created };
  },

  async getMessages(conversationId, options = {}) {
    const limit = Math.max(1, Math.min(100, Number(options.limit) || 50));
    const cursor = options.cursor || null;
    const q = new URLSearchParams();
    q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    const path = `/me/conversations/${encodeURIComponent(conversationId)}/messages?${q.toString()}`;
    const data = await request(path);
    return { messages: data.messages || [], next_cursor: data.next_cursor || null };
  },

  async sendMessage(conversationId, { text, attachments } = {}) {
    const body = {
      text: text != null ? String(text).trim() : '',
      attachments: Array.isArray(attachments) ? attachments : [],
    };
    const data = await request(`/me/conversations/${encodeURIComponent(conversationId)}/messages`, {
      method: 'POST',
      body,
    });
    return data.message || null;
  },

  async markAsRead(messageId) {
    await request(`/me/messages/${encodeURIComponent(messageId)}/read`, { method: 'PUT' });
    return true;
  },

  async markConversationRead(conversationId) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/read`, { method: 'PUT' });
    return true;
  },

  async markConversationUnread(conversationId) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/unread`, { method: 'PUT' });
    return true;
  },

  async labelConversationJobs(conversationId) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/label`, {
      method: 'PUT',
      body: { label: 'jobs' },
    });
    return true;
  },

  async starConversation(conversationId, starred = true) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/star`, {
      method: 'PUT',
      body: { starred },
    });
    return true;
  },

  async muteConversation(conversationId, muted = true) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/mute`, {
      method: 'PUT',
      body: { muted },
    });
    return true;
  },

  async reportConversation(conversationId, { report = true, block = false, target_user_id } = {}) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/report`, {
      method: 'POST',
      body: { report, block, target_user_id },
    });
    return true;
  },

  async deleteMessage(messageId) {
    await request(`/me/messages/${encodeURIComponent(messageId)}`, { method: 'DELETE' });
    return true;
  },

  async archiveConversation(conversationId, archive = true) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}/archive`, {
      method: 'PUT',
      body: { archive },
    });
    return true;
  },

  async deleteConversation(conversationId) {
    await request(`/me/conversations/${encodeURIComponent(conversationId)}`, { method: 'DELETE' });
    return true;
  },

  async getAwayMessage() {
    const data = await request('/me/messaging/away');
    return {
      enabled: !!data.enabled,
      message: data.message || '',
      start_at: data.start_at || null,
      end_at: data.end_at || null,
    };
  },

  async setAwayMessage({ enabled, message, start_at, end_at } = {}) {
    const data = await request('/me/messaging/away', {
      method: 'PUT',
      body: { enabled: !!enabled, message: message || '', start_at: start_at || null, end_at: end_at || null },
    });
    return data;
  },

  async getUnreadCount() {
    const data = await request('/me/messages/unread-count');
    return data.unread_count ?? 0;
  },

  async getAttachmentDownloadUrl(conversationId, key) {
    const q = new URLSearchParams({ key });
    const data = await request(
      `/me/conversations/${encodeURIComponent(conversationId)}/attachments/download?${q.toString()}`
    );
    return data.getUrl || null;
  },

  async getOwnAttachmentDownloadUrl(key) {
    const data = await request(`/uploads/presign-get?key=${encodeURIComponent(key)}`);
    return data.getUrl || null;
  },

  uploadAttachment(file) {
    if (file.size > MAX_ATTACHMENT_SIZE) throw new Error('File too large (max 25MB)');
    return doUploadAttachment(file);
  },
};

export default messagingAPI;
